<template>
  <v-main>
    <loader :loading="loading"></loader>
    <v-container fluid>
      <div class="breadcrumbs-container">
        <Breadcrumbs />
      </div>
      <div class="flex flex-col space-y-6">
        <!-- SBOM Hero Section -->
        <v-card class="mb-6 sbom-hero-card" elevation="4">
          <!-- Gradient Header -->
          <div class="sbom-hero-header">
            <div class="sbom-hero-header-content">
              <div class="d-flex align-center mb-2">
                <v-avatar size="48" class="mr-3 sbom-hero-logo">
                  <v-icon size="28" color="white">mdi-package-variant-closed</v-icon>
                </v-avatar>
                <div>
                  <h1 class="sbom-hero-title">SBOM Management</h1>
                  <p class="sbom-hero-subtitle">
                    Software Bill of Materials &mdash; Vulnerability Analysis & Tracking
                  </p>
                </div>
              </div>
            </div>
          </div>

          <v-card-text class="pt-6">
            <!-- Description -->
            <p class="text-body-1 mb-5 sbom-intro-text">
              Upload, process, and analyze your
              <strong>Software Bill of Materials (SBOM)</strong> files. Generate comprehensive
              vulnerability and impact reports using <strong>VimPact</strong>, visualize dependency
              trees, and integrate with <strong>Dependency Track</strong>.
            </p>

            <!-- Workflow Section -->
            <div class="sbom-workflow-section mb-6">
              <div class="sbom-section-header mb-4">
                <v-icon color="primary" size="28" class="mr-2">mdi-rocket-launch</v-icon>
                <span class="text-h6 font-weight-bold">Getting Started &mdash; SBOM Workflow</span>
              </div>

              <v-row class="sbom-workflow-steps">
                <!-- Step 1: Upload -->
                <v-col cols="12" md="4" class="sbom-workflow-col">
                  <div class="sbom-ws step-upload">
                    <div class="sbom-step-number">1</div>
                    <div class="sbom-step-icon-wrapper upload-icon">
                      <v-icon size="40" color="white">mdi-cloud-upload</v-icon>
                    </div>
                    <div class="sbom-step-title">Upload SBOM Files</div>
                    <div class="sbom-step-description">
                      Upload your SBOM files in JSON format to analyze vulnerabilities and map
                      dependencies.
                    </div>
                  </div>
                </v-col>

                <!-- Step 2: Process -->
                <v-col cols="12" md="4" class="sbom-workflow-col">
                  <div class="sbom-ws step-process">
                    <div class="sbom-step-number process-num">2</div>
                    <div class="sbom-step-icon-wrapper process-icon">
                      <v-icon size="40" color="white">mdi-cog-sync</v-icon>
                    </div>
                    <div class="sbom-step-title">Process with VimPact</div>
                    <div class="sbom-step-description">
                      Enrich SBOM files. Run vulnerability and impact analysis to generate
                      comprehensive reports (CSV, Excel, images).
                    </div>
                  </div>
                </v-col>

                <!-- Step 3: Analyze -->
                <v-col cols="12" md="4" class="sbom-workflow-col">
                  <div class="sbom-ws step-analyze">
                    <div class="sbom-step-number analyze-num">3</div>
                    <div class="sbom-step-icon-wrapper analyze-icon">
                      <v-icon size="40" color="white">mdi-chart-box-outline</v-icon>
                    </div>
                    <div class="sbom-step-title">Analyze &amp; Download</div>
                    <div class="sbom-step-description">
                      View generated reports, visualize dependency trees, and integrate with
                      Dependency Track.
                    </div>
                  </div>
                </v-col>
              </v-row>
            </div>

            <v-divider class="my-5"></v-divider>

            <!-- Features & Tools Grid -->
            <v-row>
              <v-col cols="12" md="6">
                <div class="sbom-feature-box">
                  <div class="sbom-feature-box-header">
                    <v-icon color="deep-purple" size="28" class="mr-2">mdi-chart-box</v-icon>
                    <span class="text-h6 font-weight-bold">SBOM Visualizer</span>
                  </div>
                  <p class="text-body-2 grey--text text--darken-1 mt-2 mb-3">
                    Interactive dependency exploration for your software components.
                  </p>
                  <ul class="sbom-feature-list">
                    <li>
                      <v-icon small color="deep-purple" class="mr-2">mdi-check-circle</v-icon>
                      Dependency tree visualization
                    </li>
                    <li>
                      <v-icon small color="deep-purple" class="mr-2">mdi-check-circle</v-icon>
                      Component relationship mapping
                    </li>
                    <li>
                      <v-icon small color="deep-purple" class="mr-2">mdi-check-circle</v-icon>
                      Click &ldquo;Visualize&rdquo; on any uploaded SBOM
                    </li>
                  </ul>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <div class="sbom-feature-box tools-box">
                  <div class="sbom-feature-box-header">
                    <img
                      src="/dt-logo-symbol.svg"
                      alt="Dependency-Track"
                      style="width: 28px; height: 28px; margin-right: 8px;"
                    />
                    <span class="text-h6 font-weight-bold">Dependency-Track</span>
                  </div>
                  <p class="text-body-2 grey--text text--darken-1 mt-2 mb-3">
                    Advanced vulnerability management and continuous monitoring.
                  </p>
                  <ul class="sbom-feature-list">
                    <li>
                      <v-icon small color="indigo" class="mr-2">mdi-check-circle</v-icon>
                      Import SBOMs for tracking
                    </li>
                    <li>
                      <v-icon small color="indigo" class="mr-2">mdi-check-circle</v-icon>
                      Continuous vulnerability monitoring
                    </li>
                    <li>
                      <v-icon small color="indigo" class="mr-2">mdi-check-circle</v-icon>
                      Policy compliance evaluation
                    </li>
                  </ul>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- SBOM Metrics Cards -->
        <v-row class="mb-2">
          <v-col cols="12" sm="6" lg="3">
            <v-card class="sbom-metric-card primary-grad" dark>
              <v-card-text class="d-flex flex-column align-center py-6">
                <v-icon size="48" class="mb-2">mdi-file-document-multiple</v-icon>
                <div class="text-h3 font-weight-bold">{{ uploadedFiles.length }}</div>
                <div class="text-subtitle-1">Raw SBOMs</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" lg="3">
            <v-card class="sbom-metric-card secondary-grad" dark>
              <v-card-text class="d-flex flex-column align-center py-6">
                <v-icon size="48" class="mb-2">mdi mdi-auto-fix</v-icon>
                <div class="text-h3 font-weight-bold">{{ enrichedFiles.length }}</div>
                <div class="text-subtitle-1">Enriched SBOMs</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" lg="3">
            <v-card class="sbom-metric-card accent-grad" dark>
              <v-card-text class="d-flex flex-column align-center py-6">
                <v-icon size="48" class="mb-2">mdi-check-decagram</v-icon>
                <div class="text-h3 font-weight-bold">
                  {{ enrichedFiles.filter(f => f.processed).length }}
                </div>
                <div class="text-subtitle-1">Processed Files</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" lg="3">
            <v-card class="sbom-metric-card success-grad" dark>
              <v-card-text class="d-flex flex-column align-center py-6">
                <v-icon size="48" class="mb-2">mdi-folder-download</v-icon>
                <div class="text-h3 font-weight-bold">
                  {{ Object.keys(organizedOutputFiles).length }}
                </div>
                <div class="text-subtitle-1">Output Reports</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Upload Section -->
        <v-card class="mb-6 sbom-upload-card" elevation="3">
          <v-card-title class="sbom-section-title">
            <v-icon color="white" class="mr-2">mdi-cloud-upload</v-icon>
            {{ $t('sbom.fileUpload') }}
          </v-card-title>
          <v-card-text class="pa-6">
            <div
              class="sbom-upload-zone"
              :class="{ 'drag-over': isDragOver }"
              @drop="handleDrop"
              @dragover.prevent="isDragOver = true"
              @dragleave="isDragOver = false"
              @click="triggerFileInput"
            >
              <div class="sbom-upload-icon-wrapper">
                <v-icon size="48" color="white">mdi-cloud-upload</v-icon>
              </div>
              <h3 class="text-h6 mb-2 font-weight-bold">{{ $t('sbom.dragDropFiles') }}</h3>
              <p class="text-body-2 grey--text text--darken-1 mb-4">
                {{ $t('sbom.supportedFormats') }}
              </p>
              <v-btn color="primary" class="sbom-browse-btn" elevation="2">
                <v-icon left>mdi-folder-open</v-icon>
                {{ $t('sbom.browseFiles') }}
              </v-btn>
              <input
                ref="fileInput"
                type="file"
                multiple
                @change="handleFileSelect"
                style="display: none;"
                accept=".json"
              />
            </div>
          </v-card-text>
        </v-card>

        <!-- LIST 1+2 — Raw & Enriched SBOMs side-by-side -->
        <v-row class="mb-4" align="start">
          <!-- LIST 1 — Raw Uploaded SBOMs -->
          <v-col
            v-if="rawFiles.length > 0"
            cols="12"
            :md="enrichedFiles.length > 0 ? 6 : 12"
            class="d-flex flex-column"
          >
            <v-card class="sbom-files-card flex-grow-1" elevation="3">
              <div class="sbom-list-header sbom-list-header--raw">
                <div class="d-flex align-center" style="flex:1; min-width:0;">
                  <v-avatar size="32" class="mr-3" style="background:rgba(255,255,255,0.18);">
                    <v-icon color="white" size="18">mdi-file-upload-outline</v-icon>
                  </v-avatar>
                  <div>
                    <div class="white--text text-subtitle-2 font-weight-bold">
                      Raw SBOMs
                      <v-chip
                        x-small
                        color="rgba(255,255,255,0.25)"
                        text-color="white"
                        class="ml-2"
                      >
                        {{ rawFiles.length }}
                      </v-chip>
                    </div>
                    <div class="white--text text-caption" style="opacity:.75;">
                      Enrich to unlock VimPact processing
                    </div>
                  </div>
                </div>
                <v-btn text x-small color="white" @click="clearAllFiles" class="sbom-list-del-btn">
                  <v-icon left x-small>mdi-delete-sweep</v-icon> Clear All
                </v-btn>
              </div>

              <v-card-text class="pa-0">
                <transition-group name="sbom-row" tag="div">
                  <div
                    v-for="file in rawFiles"
                    :key="file.name"
                    class="sbom-file-row"
                    :class="{ 'sbom-file-row--enriching': file.enriching }"
                  >
                    <!-- Left: icon + info -->
                    <div class="sbom-file-info">
                      <v-icon size="26" color="#ff8f00" class="mr-3">mdi-code-json</v-icon>
                      <div class="sbom-file-meta">
                        <div class="sbom-file-name">{{ file.name }}</div>
                        <div class="sbom-file-sub">
                          {{ formatFileSize(file.size) }} &bull; JSON
                          <v-chip v-if="file.processed" x-small color="success" dark class="ml-2">
                            <v-icon left x-small>mdi-check</v-icon>processed
                          </v-chip>
                          <v-chip
                            v-if="file.enriching"
                            x-small
                            color="deep-purple"
                            dark
                            class="ml-2"
                          >
                            <v-icon left x-small>mdi-loading mdi-spin</v-icon>enriching…
                          </v-chip>
                        </div>
                      </div>
                    </div>

                    <!-- Right: actions -->
                    <div class="sbom-file-actions">
                      <!-- Enrich — PRIMARY action for raw files -->
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            small
                            color="deep-purple"
                            class="sbom-action-chip sbom-action-chip--enrich white--text"
                            :loading="file.enriching"
                            :disabled="!file.fromMinio || file.enriching"
                            @click="enrichSbomFile(file)"
                            v-bind="attrs"
                            v-on="on"
                            elevation="0"
                          >
                            <v-icon left small>mdi-auto-fix</v-icon>Enrich SBOM
                          </v-btn>
                        </template>
                        <span>Enrich this raw SBOM with VimPact CVE data</span>
                      </v-tooltip>

                      <v-divider vertical class="mx-2" style="opacity:.25;"></v-divider>

                      <!-- Visualize -->
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            small
                            class="g-action-btn g-action-success"
                            :disabled="!file.fromMinio"
                            @click="visualizeSbomFile(file)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon small>mdi-package-variant</v-icon>
                          </v-btn>
                        </template>
                        <span>Visualize dependency tree</span>
                      </v-tooltip>

                      <!-- Import to DT -->
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            small
                            class="g-action-btn g-action-info"
                            :disabled="!file.fromMinio"
                            @click="importToDependencyTrack(file)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <img
                              src="/dt-logo-symbol.svg"
                              alt="DT"
                              style="width:20px;height:20px;"
                            />
                          </v-btn>
                        </template>
                        <span>Import to Dependency-Track</span>
                      </v-tooltip>

                      <!-- Delete -->
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            small
                            class="g-action-btn g-action-delete"
                            @click="removeFileByName(file.name, 'raw')"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon small>mdi-delete</v-icon>
                          </v-btn>
                        </template>
                        <span>Remove file</span>
                      </v-tooltip>
                    </div>
                  </div>
                </transition-group>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- LIST 2 — Enriched SBOMs -->
          <v-col
            v-if="enrichedFiles.length > 0"
            cols="12"
            :md="rawFiles.length > 0 ? 6 : 12"
            class="d-flex flex-column"
          >
            <v-card class="sbom-files-card sbom-enriched-card flex-grow-1" elevation="3">
              <div class="sbom-list-header sbom-list-header--enriched">
                <div class="d-flex align-center" style="flex:1; min-width:0;">
                  <v-avatar size="32" class="mr-3" style="background:rgba(255,255,255,0.18);">
                    <v-icon color="white" size="18">mdi mdi-auto-fix</v-icon>
                  </v-avatar>
                  <div>
                    <div class="white--text text-subtitle-2 font-weight-bold">
                      Enriched SBOMs
                      <v-chip
                        x-small
                        color="rgba(255,255,255,0.25)"
                        text-color="white"
                        class="ml-2"
                      >
                        {{ enrichedFiles.length }}
                      </v-chip>
                    </div>
                    <div class="white--text text-caption" style="opacity:.75;">
                      Ready for VimPact analysis — click Process to generate reports
                    </div>
                  </div>
                </div>
                <!-- Batch process all enriched -->
                <v-btn
                  v-if="enrichedFiles.filter(f => !f.processed).length > 1"
                  small
                  color="white"
                  class="sbom-batch-enrich-btn"
                  :loading="batchProcessing"
                  @click="processAllEnriched"
                  elevation="0"
                >
                  <v-icon left small>mdi-play-speed</v-icon>
                  Process All ({{ enrichedFiles.filter(f => !f.processed).length }})
                </v-btn>
              </div>

              <v-card-text class="pa-0">
                <transition-group name="sbom-row" tag="div">
                  <div
                    v-for="file in enrichedFiles"
                    :key="file.name"
                    class="sbom-file-row sbom-file-row--enriched"
                    :class="{
                      'sbom-file-row--processing': file.processing,
                      'sbom-file-row--done': file.processed,
                    }"
                  >
                    <!-- Left: icon + info -->
                    <div class="sbom-file-info">
                      <div class="sbom-enriched-badge mr-3">
                        <v-icon size="18" color="white">mdi mdi-auto-fix</v-icon>
                      </div>
                      <div class="sbom-file-meta">
                        <div class="sbom-file-name">{{ file.name }}</div>
                        <div class="sbom-file-sub">
                          {{ formatFileSize(file.size) }} &bull; Enriched SBOM
                          <v-chip v-if="file.processing" x-small color="primary" dark class="ml-2">
                            <v-icon left x-small class="mdi-spin">mdi-cog</v-icon>processing…
                          </v-chip>
                          <v-chip
                            v-else-if="file.processed"
                            x-small
                            color="success"
                            dark
                            class="ml-2"
                          >
                            <v-icon left x-small>mdi-check-circle</v-icon>reports ready
                          </v-chip>
                          <v-chip v-else-if="file.error" x-small color="error" dark class="ml-2">
                            <v-icon left x-small>mdi-alert-circle</v-icon>failed
                          </v-chip>
                          <v-chip v-else x-small outlined color="primary" class="ml-2">
                            ready to process
                          </v-chip>
                        </div>
                      </div>
                    </div>

                    <!-- Progress bar while processing -->
                    <v-progress-linear
                      v-if="file.processing"
                      indeterminate
                      color="primary"
                      class="sbom-processing-bar"
                      height="2"
                    ></v-progress-linear>

                    <!-- Right: actions -->
                    <div class="sbom-file-actions">
                      <!-- Process — PRIMARY action for enriched files -->
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            small
                            :color="file.processed ? 'success' : 'primary'"
                            class="sbom-action-chip"
                            :loading="file.processing"
                            :disabled="!file.fromMinio || file.processing"
                            @click="processSingleFile(file)"
                            v-bind="attrs"
                            v-on="on"
                            elevation="0"
                          >
                            <v-icon left small>{{
                              file.processed ? 'mdi-refresh' : 'mdi-play'
                            }}</v-icon>
                            {{ file.processed ? 'Re-Process' : 'Process' }}
                          </v-btn>
                        </template>
                        <span>{{
                          file.processed
                            ? 'Re-run VimPact analysis'
                            : 'Run VimPact vulnerability analysis'
                        }}</span>
                      </v-tooltip>

                      <v-divider vertical class="mx-2" style="opacity:.25;"></v-divider>

                      <!-- Visualize -->
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            small
                            class="g-action-btn g-action-success"
                            :disabled="!file.fromMinio"
                            @click="visualizeSbomFile(file)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon small>mdi-package-variant</v-icon>
                          </v-btn>
                        </template>
                        <span>Visualize dependency tree</span>
                      </v-tooltip>

                      <!-- Import to DT -->
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            small
                            class="g-action-btn g-action-info"
                            :disabled="!file.fromMinio"
                            @click="importToDependencyTrack(file)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <img
                              src="/dt-logo-symbol.svg"
                              alt="DT"
                              style="width:20px;height:20px;"
                            />
                          </v-btn>
                        </template>
                        <span>Import to Dependency-Track</span>
                      </v-tooltip>

                      <!-- Delete -->
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            small
                            class="g-action-btn g-action-delete"
                            @click="removeFileByName(file.name, 'enriched')"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon small>mdi-delete</v-icon>
                          </v-btn>
                        </template>
                        <span>Remove file</span>
                      </v-tooltip>
                    </div>
                  </div>
                </transition-group>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Empty State -->
        <v-card
          v-if="uploadedFiles.length === 0 && enrichedFiles.length === 0 && !loading"
          class="sbom-empty-state"
          elevation="2"
        >
          <v-card-text class="text-center pa-12">
            <div class="sbom-empty-icon-wrapper mb-6">
              <v-icon size="64" color="white">mdi-file-upload-outline</v-icon>
            </div>
            <h3 class="text-h5 font-weight-bold mb-3">{{ $t('sbom.noFilesUploaded') }}</h3>
            <p class="text-body-1 grey--text text--darken-1 mb-0">
              {{ $t('sbom.getStartedMessage') }}
            </p>
          </v-card-text>
        </v-card>
        <!-- PROCESSED OUTPUT FILES — Accordion with per-SBOM matrix gallery -->
        <v-card class="mb-6 sbom-output-card" elevation="3">
          <!-- Section Header -->
          <div class="sbom-output-header">
            <div class="sbom-output-header-left">
              <v-icon color="white" size="22" class="mr-2">mdi-folder-open-outline</v-icon>
              <span class="sbom-output-header-title">Processed Output Files</span>
              <v-chip
                small
                dark
                class="ml-3 sbom-output-count-chip"
                v-if="!loadingOutputFiles && Object.keys(organizedOutputFiles).length > 0"
              >
                {{ Object.keys(organizedOutputFiles).length }}
                {{
                  Object.keys(organizedOutputFiles).length === 1 ? 'source file' : 'source files'
                }}
              </v-chip>
            </div>
            <v-btn
              small
              text
              dark
              @click="loadOutputFiles"
              :loading="loadingOutputFiles"
              class="sbom-output-refresh-btn"
            >
              <v-icon left small>mdi-refresh</v-icon>
              Refresh
            </v-btn>
          </div>

          <!-- Body -->
          <v-card-text class="pa-0">
            <!-- Loading -->
            <div v-if="loadingOutputFiles" class="sbom-output-loading">
              <v-progress-circular indeterminate color="primary" size="36"></v-progress-circular>
              <p class="mt-3 text-body-2 grey--text">Loading processed files…</p>
            </div>

            <!-- Empty -->
            <div
              v-else-if="Object.keys(organizedOutputFiles).length === 0"
              class="sbom-output-empty"
            >
              <v-icon size="52" color="#e0e0e0" class="mb-3">mdi-file-search-outline</v-icon>
              <p class="sbom-output-empty-title">No report files yet</p>
              <p class="sbom-output-empty-hint">
                Process an enriched SBOM to generate Excel, CSV and JSON reports
              </p>
            </div>

            <!-- Accordion — one panel per processed SBOM source file -->
            <div v-else class="sbom-output-panels-wrap">
              <v-expansion-panels v-model="outputFilesPanel" multiple flat>
                <v-expansion-panel
                  v-for="(categories, processedFileName) in organizedOutputFiles"
                  :key="processedFileName"
                  class="sbom-output-panel"
                >
                  <!-- Panel header -->
                  <v-expansion-panel-header class="sbom-output-panel-header">
                    <div class="d-flex align-center min-width-0">
                      <v-avatar size="30" tile class="mr-3 sbom-output-panel-avatar">
                        <v-icon size="18" color="white">mdi-file-document-outline</v-icon>
                      </v-avatar>
                      <span class="sbom-output-panel-name">{{ processedFileName }}</span>
                    </div>
                    <template #actions>
                      <div class="d-flex align-center mr-2" @click.stop>
                        <v-chip
                          x-small
                          class="mr-2 sbom-output-panel-img-chip"
                          v-if="getMatrixFilesForSource(categories).length > 0"
                        >
                          <v-icon left x-small>mdi-chart-scatter-plot</v-icon>
                          {{ getMatrixFilesForSource(categories).length }}
                          {{
                            getMatrixFilesForSource(categories).length === 1 ? 'matrix' : 'matrices'
                          }}
                        </v-chip>
                        <v-chip x-small class="sbom-output-panel-file-chip">
                          <v-icon left x-small>mdi-file-multiple-outline</v-icon>
                          {{ getNonImageFilesCount(categories) }} reports
                        </v-chip>
                      </div>
                      <v-icon>mdi-chevron-down</v-icon>
                    </template>
                  </v-expansion-panel-header>

                  <v-expansion-panel-content class="sbom-output-panel-content">
                    <v-row dense class="sbom-panel-body-row ma-0">
                      <!-- Left: matrix images -->
                      <v-col
                        v-if="getMatrixFilesForSource(categories).length > 0"
                        cols="12"
                        md="7"
                        class="sbom-panel-images-col pr-md-3"
                      >
                        <div class="sbom-inline-matrix-label">
                          <v-icon size="16" color="#7c4dff" class="mr-1"
                            >mdi-chart-scatter-plot</v-icon
                          >
                          VimPact Analysis Matrices
                          <span class="sbom-inline-matrix-hint">Click to expand full screen</span>
                        </div>

                        <div
                          v-for="(imgFile, idx) in getMatrixFilesForSource(categories)"
                          :key="imgFile.filePath"
                          class="mb-2"
                        >
                          <div
                            class="sbom-matrix-card"
                            :class="'sbom-matrix-card--' + (idx % 4)"
                            @click="previewImage(imgFile)"
                          >
                            <div class="sbom-matrix-card-header">
                              <div class="sbom-matrix-card-type-badge">
                                <v-icon x-small color="white" class="mr-1"
                                  >mdi-chart-scatter-plot</v-icon
                                >
                                <span>{{ generateImageTitle(imgFile.fileName) }}</span>
                              </div>
                            </div>

                            <div class="sbom-matrix-img-container">
                              <div
                                v-if="matrixImageLoadingStates[imgFile.filePath] === 'loading'"
                                class="sbom-matrix-img-loader"
                              >
                                <v-progress-circular
                                  indeterminate
                                  color="white"
                                  size="28"
                                ></v-progress-circular>
                              </div>
                              <div
                                v-else-if="matrixImageLoadingStates[imgFile.filePath] === 'error'"
                                class="sbom-matrix-img-error"
                              >
                                <v-icon size="36" color="rgba(255,255,255,0.4)"
                                  >mdi-image-broken-variant</v-icon
                                >
                                <p class="sbom-matrix-img-error-text">Could not load</p>
                              </div>
                              <img
                                v-else-if="matrixImageUrls[imgFile.filePath]"
                                :src="matrixImageUrls[imgFile.filePath]"
                                :alt="generateImageTitle(imgFile.fileName) + ' — VimPact Analysis'"
                                class="sbom-matrix-img"
                              />
                              <div v-else class="sbom-matrix-img-loader">
                                <v-progress-circular
                                  indeterminate
                                  color="white"
                                  size="28"
                                ></v-progress-circular>
                              </div>
                            </div>

                            <div class="sbom-matrix-card-footer">
                              <p class="sbom-matrix-card-desc">
                                {{ generateImageDescription(imgFile.fileName) }}
                              </p>
                              <div class="sbom-matrix-card-actions">
                                <v-btn
                                  x-small
                                  rounded
                                  dark
                                  class="sbom-matrix-preview-btn"
                                  @click.stop="previewImage(imgFile)"
                                  :loading="downloadingFiles[imgFile.filePath]"
                                >
                                  <v-icon left x-small>mdi-fullscreen</v-icon>
                                  Expand
                                </v-btn>
                                <v-btn
                                  x-small
                                  rounded
                                  outlined
                                  dark
                                  class="sbom-matrix-dl-btn ml-2"
                                  @click.stop="downloadOutputFile(imgFile)"
                                  :loading="downloadingFiles[imgFile.filePath]"
                                >
                                  <v-icon left x-small>mdi-download</v-icon>
                                  Download
                                </v-btn>
                              </div>
                            </div>

                            <div class="sbom-matrix-hover-overlay">
                              <v-icon size="36" color="white">mdi-fullscreen</v-icon>
                            </div>
                          </div>
                        </div>
                      </v-col>

                      <!-- Right: report files -->
                      <v-col
                        cols="12"
                        :md="getMatrixFilesForSource(categories).length > 0 ? 5 : 12"
                        class="sbom-panel-files-col"
                      >
                        <div
                          class="sbom-panel-files-label"
                          v-if="getMatrixFilesForSource(categories).length > 0"
                        >
                          <v-icon size="16" color="#1a237e" class="mr-1"
                            >mdi-file-multiple-outline</v-icon
                          >
                          Download Reports
                        </div>
                        <template v-for="(files, category) in categories">
                          <div
                            v-if="!isCategoryImages(category)"
                            :key="category"
                            class="sbom-output-category-block mb-2"
                          >
                            <div class="sbom-output-category-label">
                              <v-icon left small :color="getCategoryColor(category)">{{
                                getCategoryIcon(category)
                              }}</v-icon>
                              <span>{{ getCategoryLabel(category) }}</span>
                              <v-chip
                                x-small
                                outlined
                                :color="getCategoryColor(category)"
                                class="ml-auto"
                              >
                                {{ files.length }}
                              </v-chip>
                            </div>

                            <div
                              v-for="file in files"
                              :key="file.fileName"
                              class="sbom-output-file-row"
                            >
                              <v-icon
                                small
                                :color="getFileTypeColor(file.fileName)"
                                class="mr-2 flex-shrink-0"
                              >
                                {{ getFileTypeIcon(file.fileName) }}
                              </v-icon>
                              <span class="sbom-output-file-name" :title="file.fileName">{{
                                file.fileName
                              }}</span>
                              <v-tooltip top v-if="isUploadedAsEvidence(file.fileName)">
                                <template #activator="{ on, attrs }">
                                  <v-chip
                                    x-small
                                    outlined
                                    color="#2e7d32"
                                    class="sbom-output-evidence-badge ml-2"
                                    v-bind="attrs"
                                    v-on="on"
                                  >
                                    <v-icon x-small left>mdi-check-decagram</v-icon>
                                    Evidence
                                  </v-chip>
                                </template>
                                <span>Already uploaded as evidence</span>
                              </v-tooltip>
                              <div class="sbom-output-file-actions">
                                <v-tooltip top v-if="canPreviewFile(file.fileName)">
                                  <template #activator="{ on, attrs }">
                                    <v-btn
                                      icon
                                      x-small
                                      v-bind="attrs"
                                      v-on="on"
                                      @click="previewFile(file)"
                                      class="sbom-output-file-btn"
                                    >
                                      <v-icon size="16" color="#1a237e">mdi-eye-outline</v-icon>
                                    </v-btn>
                                  </template>
                                  <span>Preview</span>
                                </v-tooltip>
                                <v-tooltip top>
                                  <template #activator="{ on, attrs }">
                                    <v-btn
                                      icon
                                      x-small
                                      v-bind="attrs"
                                      v-on="on"
                                      @click="downloadOutputFile(file)"
                                      :loading="downloadingFiles[file.filePath]"
                                      class="sbom-output-file-btn"
                                    >
                                      <v-icon size="16" color="#00695c"
                                        >mdi-download-outline</v-icon
                                      >
                                    </v-btn>
                                  </template>
                                  <span>Download</span>
                                </v-tooltip>
                                <v-tooltip top>
                                  <template #activator="{ on, attrs }">
                                    <v-btn
                                      icon
                                      x-small
                                      v-bind="attrs"
                                      v-on="on"
                                      @click="uploadFileAsEvidence(file)"
                                      :loading="evidenceUploadLoading[file.filePath]"
                                      class="sbom-output-file-btn"
                                    >
                                      <v-icon
                                        size="16"
                                        :color="
                                          isUploadedAsEvidence(file.fileName)
                                            ? '#2e7d32'
                                            : '#1a237e'
                                        "
                                        >{{
                                          isUploadedAsEvidence(file.fileName)
                                            ? 'mdi-check-decagram'
                                            : 'mdi-file-certificate-outline'
                                        }}</v-icon
                                      >
                                    </v-btn>
                                  </template>
                                  <span>{{
                                    isUploadedAsEvidence(file.fileName)
                                      ? 'Already uploaded as evidence — click to upload again'
                                      : 'Upload as Evidence'
                                  }}</span>
                                </v-tooltip>
                              </div>
                            </div>
                          </div>
                        </template>
                      </v-col>
                    </v-row>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </v-card-text>
        </v-card>
        <!-- Dependency Track Card -->
        <v-card class="mb-6 sbom-dt-card" elevation="3">
          <div class="sbom-dt-header">
            <div class="sbom-dt-content">
              <div class="d-flex align-center">
                <v-avatar size="48" class="mr-3 sbom-dt-logo-avatar" tile>
                  <img src="/dt-logo-symbol.svg" alt="Dependency-Track" class="sbom-dt-logo-img" />
                </v-avatar>
                <div>
                  <h3 class="white--text font-weight-bold text-h6 mb-0">Dependency-Track</h3>
                  <p class="white--text text--lighten-2 text-body-2 mb-0" style="opacity: 0.85;">
                    {{ $t('sbom.processDescription') }}
                  </p>
                </div>
              </div>
            </div>
            <v-btn color="white" class="sbom-dt-btn" @click="dependencyTrack" elevation="2">
              <v-icon left color="primary">mdi-open-in-new</v-icon>
              Open Dependency-Track
            </v-btn>
          </div>
        </v-card>
      </div>
    </v-container>

    <!-- Processing Progress Dialog -->
    <v-dialog v-model="progressDialog" max-width="600" persistent>
      <v-card class="sbom-dialog-card">
        <v-card-title class="sbom-dialog-title">
          <v-icon left color="white">mdi-cog-sync</v-icon>
          {{ $t('sbom.processingFiles') }}
        </v-card-title>
        <v-card-text class="pt-4">
          <div class="mb-4">
            <v-progress-linear
              :value="progressPercentage"
              color="primary"
              height="8"
              rounded
            ></v-progress-linear>
            <div class="text-center mt-2 text-body-2">
              {{ processedCount }} / {{ totalToProcess }} {{ $t('sbom.filesCompleted') }}
            </div>
          </div>

          <v-list dense>
            <v-list-item v-for="file in processQueue" :key="file.name">
              <v-list-item-avatar>
                <v-icon :color="getProcessingStatusColor(file)">
                  {{ getProcessingStatusIcon(file) }}
                </v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ file.name }}</v-list-item-title>
                <v-list-item-subtitle>{{ getProcessingStatusText(file) }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions v-if="!batchProcessing">
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="progressDialog = false">
            {{ $t('close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success Dialog -->
    <v-dialog v-model="successDialog" max-width="500">
      <v-card class="sbom-dialog-card">
        <v-card-title class="sbom-dialog-title-success">
          <v-icon left color="white" size="28">mdi-check-circle</v-icon>
          {{ $t('sbom.processingComplete') }}
        </v-card-title>
        <v-card-text class="pt-4">
          <div class="text-body-1 mb-3">
            {{ $t('sbom.filesProcessedSuccessfully') }}
          </div>
          <v-alert type="info" outlined dense class="mb-0">
            <div class="text-body-2">
              <strong>{{ $t('sbom.generatedFiles') }}:</strong><br />
              <code class="text-caption">{{ generatedFilePath }}</code>
            </div>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="success" text @click="successDialog = false">
            {{ $t('close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Image Preview Modal -->
    <ImagePreviewModal />

    <!-- Add FilePreviewDialog before the closing template tag -->
    <FilePreviewDialog
      ref="filePreviewDialog"
      v-model="previewDialog"
      :file="selectedFileForPreview"
      :on-download="downloadOutputFile"
      @load-file="handleFileLoad"
    />

    <!-- Upload Report as Evidence -->
    <EvidenceQuickCreateDialog
      v-model="evidenceDialogOpen"
      :file="evidenceDialogFile"
      :default-title="evidenceDialogDefaultTitle"
      @created="handleEvidenceCreated"
    />
  </v-main>
</template>

<script>
import Loader from '@/components/helpers/Loader.vue';
import Breadcrumbs from '@/components/charts/Breadcrumbs.vue';
import ImagePreviewModal from '@/components/helpers/ImagePreviewModal.vue';
import FilePreviewDialog from '@/components/helpers/FilePreviewDialog.vue';
import EvidenceQuickCreateDialog from '@/modules/evidence/components/EvidenceQuickCreateDialog.vue';
import evidenceApi from '@/modules/evidence/api/evidenceApi';
import EventBus from '@/helpers/event-bus.js';
import util from '@/mixins/util.js';
import { SbomApi } from '@/modules/sbom/api';
import dependencyTrackApi from '../api/dependencyTrackApi';
import sbomApi from '../api/sbomApi';

export default {
  name: 'Sbom',
  mixins: [util],
  components: {
    Loader,
    Breadcrumbs,
    ImagePreviewModal,
    FilePreviewDialog,
    EvidenceQuickCreateDialog,
  },
  data() {
    return {
      loading: false,
      processing: false,
      isDragOver: false,
      uploadedFiles: [],
      vimpactOutputFiles: [], // Add this to track output files
      successDialog: false,
      generatedFilePath: '',
      selectedFiles: [],
      selectAll: false,
      batchProcessing: false,
      progressDialog: false,
      processQueue: [],
      processedCount: 0,
      totalToProcess: 0,
      organizedOutputFiles: {},
      loadingOutputFiles: false,
      downloadingFiles: {},
      outputFilesPanel: [],
      outputActiveTab: 0,
      contextField: '', // Add context field

      // Matrix image inline preview cache
      matrixImageUrls: {},
      matrixImageLoadingStates: {}, // 'loading' | 'ready' | 'error'

      // Preview dialog state
      previewDialog: false,
      selectedFileForPreview: null,

      // Enriched files — populated by enrichSbomFile() and loadExistingFiles()
      enrichedFiles: [],

      // Upload report as Evidence
      evidenceUploadLoading: {},
      evidenceDialogOpen: false,
      evidenceDialogFile: null,
      evidenceDialogDefaultTitle: '',
      uploadedEvidenceFileNames: new Set(),
    };
  },
  async mounted() {
    await this.loadExistingFiles();
    this.loadOutputFiles();
    this.loadUploadedEvidenceFileNames();
  },
  computed: {
    progressPercentage() {
      return this.totalToProcess > 0 ? (this.processedCount / this.totalToProcess) * 100 : 0;
    },
    // Raw (non-enriched) files — shown in List 1
    rawFiles() {
      return this.uploadedFiles.filter(f => !f.name.toLowerCase().includes('enriched'));
    },
    // Flat list of all matrix image files across all processed groups
    allMatrixFiles() {
      const result = [];
      Object.entries(this.organizedOutputFiles).forEach(([sourceName, categories]) => {
        Object.entries(categories).forEach(([, files]) => {
          files.forEach(file => {
            if (this.isImageFile(file.fileName)) {
              result.push({ file, sourceName });
            }
          });
        });
      });
      return result;
    },
    // organizedOutputFiles with image-only categories stripped out (used in the Output Files card)
    nonImageOrganizedOutputFiles() {
      const result = {};
      Object.entries(this.organizedOutputFiles).forEach(([sourceName, categories]) => {
        const filtered = {};
        Object.entries(categories).forEach(([category, files]) => {
          const nonImages = files.filter(f => !this.isImageFile(f.fileName));
          if (nonImages.length > 0) {
            filtered[category] = nonImages;
          }
        });
        if (Object.keys(filtered).length > 0) {
          result[sourceName] = filtered;
        }
      });
      return result;
    },
  },
  methods: {
    async loadExistingFiles() {
      this.loading = true;
      try {
        // Load both input files and output files
        const [filesResponse, outputResponse] = await Promise.all([
          SbomApi.getFiles(),
          SbomApi.getVimpactOutputFiles(),
        ]);

        this.vimpactOutputFiles = outputResponse.data;

        const allFileObjects = await Promise.all(
          filesResponse.data.map(async filename => {
            const size = (await sbomApi.getFileSize(filename)).data;
            return {
              name: filename,
              size,
              type: this.getFileType(filename),
              status: 'uploaded',
              uploadedAt: new Date(),
              fromMinio: true,
              processed: this.isFileProcessed(filename),
              processing: false,
              enriching: false,
              error: false,
            };
          }),
        );

        // Split into raw files and enriched files
        this.uploadedFiles = allFileObjects.filter(f => !f.name.toLowerCase().includes('enriched'));
        this.enrichedFiles = allFileObjects.filter(f => f.name.toLowerCase().includes('enriched'));
      } catch (error) {
        console.error('Error loading existing files:', error);
        EventBus.$emit('snackbar', {
          text: this.$t('sbom.errorLoadingFiles'),
          color: 'error',
        });
      } finally {
        this.loading = false;
      }
    },

    isFileProcessed(filename) {
      // Check if there's a corresponding output file for this input file
      // Output files typically have a suffix or are in a processed format
      const baseFileName = filename.replace('.json', '');
      return this.vimpactOutputFiles.some(
        outputFile =>
          outputFile.includes(baseFileName) ||
          outputFile.startsWith(baseFileName + '_processed') ||
          outputFile.startsWith(baseFileName + '_vimpact'),
      );
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    handleFileSelect(event) {
      const files = Array.from(event.target.files);
      this.addFiles(files);
    },

    handleDrop(event) {
      event.preventDefault();
      this.isDragOver = false;
      const files = Array.from(event.dataTransfer.files);
      this.addFiles(files);
    },

    async addFiles(files) {
      const validFiles = files.filter(file => this.isValidFile(file));

      if (validFiles.length === 0) {
        EventBus.$emit('snackbar', {
          text: this.$t('sbom.noValidFiles'),
          color: 'warning',
        });
        return;
      }

      this.loading = true;

      try {
        const formData = new FormData();
        validFiles.forEach(file => {
          formData.append('files', file);
        });

        const response = await SbomApi.upload(formData);

        // Add uploaded files to the correct list (raw vs enriched)
        const uploadedFileNames = response.data.uploadedFiles;
        uploadedFileNames.forEach(filename => {
          const originalFile = validFiles.find(f => f.name === filename);
          const fileObj = {
            name: filename,
            size: originalFile ? originalFile.size : 0,
            type: this.getFileType(filename),
            status: 'uploaded',
            uploadedAt: new Date(),
            fromMinio: true,
            processed: this.isFileProcessed(filename),
            processing: false,
            enriching: false,
            error: false,
            context: this.contextField,
          };
          if (filename.toLowerCase().includes('enriched')) {
            this.enrichedFiles.push(fileObj);
          } else {
            this.uploadedFiles.push(fileObj);
          }
        });

        EventBus.$emit('snackbar', {
          text: this.$t('sbom.filesUploadedSuccessfully', { count: uploadedFileNames.length }),
          color: 'success',
        });
      } catch (error) {
        console.error('Error uploading files:', error);
        EventBus.$emit('snackbar', {
          text: this.$t('sbom.errorUploadingFiles'),
          color: 'error',
        });
      } finally {
        this.loading = false;
      }
    },

    isValidFile(file) {
      const allowedTypes = ['application/json'];
      return allowedTypes.includes(file.type) || this.hasValidExtension(file.name);
    },

    hasValidExtension(fileName) {
      const allowedExtensions = ['.json'];
      return allowedExtensions.some(ext => fileName.toLowerCase().endsWith(ext));
    },

    async removeFile(index) {
      const fileToRemove = this.uploadedFiles[index];
      if (fileToRemove) {
        await this.removeFileByName(fileToRemove.name, 'raw');
      }
    },

    // removeFileByName — used by the two-list UI
    async removeFileByName(fileName, listType) {
      this.loading = true;
      try {
        await SbomApi.deleteFile(fileName);
        if (listType === 'enriched') {
          this.enrichedFiles = this.enrichedFiles.filter(f => f.name !== fileName);
        } else {
          this.uploadedFiles = this.uploadedFiles.filter(f => f.name !== fileName);
        }
        await this.loadOutputFiles();
        EventBus.$emit('snackbar', {
          text: this.$t('sbom.fileRemoved', { fileName }),
          color: 'info',
        });
      } catch (error) {
        console.error('Error removing file:', error);
        EventBus.$emit('snackbar', { text: this.$t('sbom.errorRemovingFile'), color: 'error' });
      } finally {
        this.loading = false;
      }
    },

    // Process all enriched files that haven't been processed yet
    async processAllEnriched() {
      const pending = this.enrichedFiles.filter(f => !f.processed && !f.processing);
      if (!pending.length) return;

      this.batchProcessing = true;
      this.progressDialog = true;
      this.processQueue = [...pending];
      this.processedCount = 0;
      this.totalToProcess = pending.length;

      for (const file of pending) {
        this.$set(file, 'processing', true);
        this.$set(file, 'error', false);
        try {
          const response = await SbomApi.processFile(file.name);
          if (response.data.status === 'success') {
            this.$set(file, 'processed', true);
          } else {
            throw new Error('Processing failed');
          }
        } catch (error) {
          this.$set(file, 'error', true);
        } finally {
          this.$set(file, 'processing', false);
          this.processedCount++;
        }
      }

      await this.loadOutputFiles();
      this.batchProcessing = false;
      EventBus.$emit('snackbar', {
        text: this.$t('sbom.filesProcessedSuccessfully'),
        color: 'success',
      });
    },

    async clearAllFiles() {
      this.loading = true;
      try {
        const response = await SbomApi.deleteAllFiles();
        this.uploadedFiles = [];
        this.enrichedFiles = [];

        // Also refresh output files since they should all be deleted
        await this.loadOutputFiles();

        // Show enhanced message from server
        const message = response.data.message || this.$t('sbom.allFilesCleared');

        EventBus.$emit('snackbar', {
          text: message,
          color: 'info',
        });
      } catch (error) {
        console.error('Error clearing all files:', error);
        EventBus.$emit('snackbar', {
          text: this.$t('sbom.errorClearingFiles'),
          color: 'error',
        });
      } finally {
        this.loading = false;
      }
    },

    async enrichSbomFile(file) {
      if (!file.fromMinio) {
        EventBus.$emit('snackbar', {
          text: this.$t('sbom.fileNotAvailable'),
          color: 'warning',
        });
        return;
      }

      this.$set(file, 'enriching', true);

      try {
        const response = await SbomApi.enrichFile(file.name);

        if (response.data.status === 'success') {
          const enrichedFileName = response.data.enrichedFile;

          // Add the enriched file to the ENRICHED list (separate from raw files)
          this.enrichedFiles.push({
            name: enrichedFileName,
            size: response.data.enrichedFileSize || 0,
            type: this.getFileType(enrichedFileName),
            status: 'uploaded',
            uploadedAt: new Date(),
            fromMinio: true,
            processed: false,
            processing: false,
            enriching: false,
            error: false,
          });

          EventBus.$emit('snackbar', {
            text: `SBOM enriched successfully. New file: ${enrichedFileName}`,
            color: 'success',
          });
        }
      } catch (error) {
        console.error('Error enriching SBOM file:', error);
        EventBus.$emit('snackbar', {
          text: `Error enriching ${file.name}: ${error.response?.data?.error || error.message}`,
          color: 'error',
        });
      } finally {
        this.$set(file, 'enriching', false);
      }
    },

    async processSingleFile(file) {
      if (!file.fromMinio) {
        EventBus.$emit('snackbar', {
          text: this.$t('sbom.fileNotAvailable'),
          color: 'warning',
        });
        return;
      }

      // Set processing state
      this.$set(file, 'processing', true);
      this.$set(file, 'processed', false);
      this.$set(file, 'error', false);

      try {
        const response = await SbomApi.processFile(file.name);

        if (response.data.status === 'success') {
          // Refresh output files and update processed status
          await this.refreshProcessedStatus();
          await this.loadOutputFiles();
          this.$set(file, 'processed', this.isFileProcessed(file.name));

          EventBus.$emit('snackbar', {
            text: this.$t('sbom.fileProcessedSuccessfully', { fileName: file.name }),
            color: 'success',
          });
        }
      } catch (error) {
        console.error('Error processing file:', error);
        this.$set(file, 'error', true);
        EventBus.$emit('snackbar', {
          text: this.$t('sbom.errorProcessingFile', { fileName: file.name }),
          color: 'error',
        });
      } finally {
        this.$set(file, 'processing', false);
      }
    },

    async processSelectedFiles() {
      if (this.selectedFiles.length === 0) return;

      // If all files are selected, use the processAllFiles function
      if (this.selectedFiles.length === this.uploadedFiles.length) {
        return this.processAllFiles();
      }

      this.batchProcessing = true;
      this.progressDialog = true;
      this.processQueue = [...this.selectedFiles];
      this.processedCount = 0;
      this.totalToProcess = this.selectedFiles.length;

      // Reset processing states
      this.processQueue.forEach(file => {
        this.$set(file, 'processing', false);
        this.$set(file, 'processed', false);
        this.$set(file, 'error', false);
        this.$set(file, 'queueStatus', 'pending');
      });

      // Process files sequentially to avoid overwhelming the server
      for (let i = 0; i < this.processQueue.length; i++) {
        const file = this.processQueue[i];

        try {
          this.$set(file, 'processing', true);
          this.$set(file, 'queueStatus', 'processing');

          const response = await SbomApi.processFile(file.name);

          if (response.data.status === 'success') {
            this.$set(file, 'processed', true);
            this.$set(file, 'queueStatus', 'completed');
          } else {
            throw new Error('Processing failed');
          }
        } catch (error) {
          console.error('Error processing file:', error);
          this.$set(file, 'error', true);
          this.$set(file, 'queueStatus', 'error');
        } finally {
          this.$set(file, 'processing', false);
          this.processedCount++;
        }
      }

      this.batchProcessing = false;
      this.clearSelection();

      // Refresh output files after batch processing is complete
      await this.refreshProcessedStatus();
      await this.loadOutputFiles();

      const successCount = this.processQueue.filter(f => f.processed).length;
      const errorCount = this.processQueue.filter(f => f.error).length;

      EventBus.$emit('snackbar', {
        text: this.$t('sbom.batchProcessingComplete', {
          success: successCount,
          error: errorCount,
          total: this.totalToProcess,
        }),
        color: errorCount === 0 ? 'success' : 'warning',
      });
    },

    async processAllFiles() {
      if (this.uploadedFiles.length === 0) {
        EventBus.$emit('snackbar', {
          text: this.$t('sbom.noFilesToProcess'),
          color: 'warning',
        });
        return;
      }

      this.processing = true;
      this.batchProcessing = true;

      try {
        const response = await SbomApi.processAllFiles();

        if (response.data.status === 'success') {
          this.generatedFilePath = `Processed ${
            response.data.count
          } files: ${response.data.processed_files.join(', ')}`;
          this.successDialog = true;

          // Refresh output files and update all processed statuses
          await this.refreshProcessedStatus();
          await this.loadOutputFiles(); // Add this line to refresh output files
          this.uploadedFiles.forEach(file => {
            this.$set(file, 'processed', this.isFileProcessed(file.name));
            this.$set(file, 'processing', false);
            this.$set(file, 'error', false);
          });

          EventBus.$emit('snackbar', {
            text: this.$t('sbom.filesProcessedSuccessfully'),
            color: 'success',
          });
        }
      } catch (error) {
        console.error('Error processing files:', error);

        // Mark all files as error
        this.uploadedFiles.forEach(file => {
          this.$set(file, 'error', true);
          this.$set(file, 'processing', false);
          this.$set(file, 'processed', false);
        });

        EventBus.$emit('snackbar', {
          text: this.$t('sbom.errorProcessingFiles'),
          color: 'error',
        });
      } finally {
        this.processing = false;
        this.batchProcessing = false;
        this.clearSelection();
      }
    },

    async refreshProcessedStatus() {
      try {
        const response = await SbomApi.getVimpactOutputFiles();
        this.vimpactOutputFiles = response.data;
      } catch (error) {
        console.error('Error refreshing VimPact output files:', error);
      }
    },

    getProcessingStatusIcon(file) {
      switch (file.queueStatus) {
        case 'processing':
          return 'mdi-cog';
        case 'completed':
          return 'mdi-check-circle';
        case 'error':
          return 'mdi-alert-circle';
        default:
          return 'mdi-clock-outline';
      }
    },

    getProcessingStatusColor(file) {
      switch (file.queueStatus) {
        case 'processing':
          return 'info';
        case 'completed':
          return 'success';
        case 'error':
          return 'error';
        default:
          return 'grey';
      }
    },

    getProcessingStatusText(file) {
      switch (file.queueStatus) {
        case 'processing':
          return this.$t('sbom.processing');
        case 'completed':
          return this.$t('sbom.completed');
        case 'error':
          return this.$t('sbom.error');
        default:
          return this.$t('sbom.pending');
      }
    },

    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedFiles = [...this.uploadedFiles];
      } else {
        this.selectedFiles = [];
      }
    },

    updateSelectAll() {
      this.selectAll = this.selectedFiles.length === this.uploadedFiles.length;
    },

    clearSelection() {
      this.selectedFiles = [];
      this.selectAll = false;
    },

    async visualizeSbomFile(file) {
      if (!file.fromMinio) {
        EventBus.$emit('snackbar', {
          text: this.$t('sbom.fileNotAvailable'),
          color: 'warning',
        });
        return;
      }

      this.loading = true;
      try {
        // Download file content from Minio
        const response = await SbomApi.downloadFile(file.name);

        // Handle different response formats
        let jsonContent;
        if (typeof response.data === 'string') {
          jsonContent = JSON.parse(response.data);
        } else if (typeof response.data === 'object') {
          jsonContent = response.data;
        } else {
          throw new Error('Invalid response format');
        }

        // Store file metadata
        const fileData = {
          name: file.name,
          size: file.size,
          type: file.type,
        };
        localStorage.setItem('sbomFileData', JSON.stringify(fileData));

        // Try to store the content, with multiple fallback methods for large files
        try {
          // First attempt: Try to use LZString compression
          const LZString = await import('lz-string').catch(() => null);

          if (LZString) {
            // Compress the JSON string
            const jsonStr = JSON.stringify(jsonContent);
            const compressed = LZString.default.compressToUTF16(jsonStr);

            try {
              localStorage.setItem('sbomJsonContent', compressed);
              localStorage.setItem('sbomJsonCompressed', 'true');
              console.log('SBOM data compressed and stored successfully');
            } catch (storageError) {
              throw new Error('File too large even when compressed');
            }
          } else {
            // If compression library not available, try storing directly
            localStorage.setItem('sbomJsonContent', JSON.stringify(jsonContent));
          }
        } catch (storageError) {
          console.warn('Primary storage method failed, trying alternative method', storageError);

          // Second attempt: Try chunking the data into multiple localStorage items
          try {
            const jsonStr = JSON.stringify(jsonContent);
            const chunkSize = 1024 * 1024; // 1MB chunks
            const chunks = Math.ceil(jsonStr.length / chunkSize);

            // If file is too big (more than 20 chunks), show error
            if (chunks > 20) {
              throw new Error('File too large to visualize in browser');
            }

            localStorage.setItem('sbomJsonChunked', 'true');
            localStorage.setItem('sbomJsonChunkCount', chunks.toString());

            for (let i = 0; i < chunks; i++) {
              const chunk = jsonStr.substring(i * chunkSize, (i + 1) * chunkSize);
              localStorage.setItem(`sbomJsonChunk_${i}`, chunk);
            }

            console.log(`SBOM data stored in ${chunks} chunks`);
          } catch (chunkError) {
            // If all else fails, show a helpful error
            throw new Error('SBOM file is too large to visualize directly in browser');
          }
        }

        // Navigate to SBOM visualizer
        this.$router.push({ name: 'SbomVisualizer' });
      } catch (error) {
        console.error('Error loading file for visualization:', error);
        EventBus.$emit('snackbar', {
          text: error.message || this.$t('sbom.errorLoadingFileForVisualization'),
          color: 'error',
        });
      } finally {
        this.loading = false;
      }
    },

    dependencyTrack() {
      // Open Dependency Track in new tab
      window.open('http://localhost:8180', '_blank');
    },

    importToDependencyTrack(file) {
      if (!file.fromMinio) {
        EventBus.$emit('snackbar', {
          text: this.$t('sbom.fileNotAvailable'),
          color: 'warning',
        });
        return;
      } else {
        dependencyTrackApi.importSbom(file.name);
      }

      // Open Dependency Track in new tab for file import
      //window.open('http://localhost:8180', '_blank');

      EventBus.$emit('snackbar', {
        text: this.$t('sbom.importingToDependencyTrack', { fileName: file.name }),
        color: 'info',
      });
    },

    getFileIcon(fileName) {
      const extension = fileName
        .split('.')
        .pop()
        .toLowerCase();
      const iconMap = {
        pdf: { icon: 'mdi-file-pdf-box', color: 'red' },
        doc: { icon: 'mdi-file-word-box', color: 'blue' },
        docx: { icon: 'mdi-file-word-box', color: 'blue' },
        txt: { icon: 'mdi-file-document', color: 'grey' },
        json: { icon: 'mdi-code-json', color: 'orange' },
        xml: { icon: 'mdi-code-tags', color: 'green' },
        csv: { icon: 'mdi-file-excel-box', color: 'green' },
      };
      return iconMap[extension] || { icon: 'mdi-file', color: 'grey' };
    },

    getFileType(fileName) {
      return fileName
        .split('.')
        .pop()
        .toUpperCase();
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1000;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    async loadOutputFiles() {
      this.loadingOutputFiles = true;
      try {
        const response = await SbomApi.getOrganizedVimpactOutputFiles();
        const rawData = response.data;

        // Reorganize data to group matrix files with their parent processed files
        this.organizedOutputFiles = {};

        // First, collect all non-matrix files
        Object.keys(rawData).forEach(processedFileName => {
          const categories = rawData[processedFileName];
          this.organizedOutputFiles[processedFileName] = { ...categories };
        });

        // Then, find matrix files and group them with their corresponding processed files
        Object.keys(rawData).forEach(processedFileName => {
          const categories = rawData[processedFileName];

          // Handle matrix, criticality, and effort categories that contain matrix images
          const matrixCategories = ['matrix', 'criticality', 'effort'];

          matrixCategories.forEach(categoryName => {
            if (categories[categoryName]) {
              // These are standalone matrix files, we need to find their parent
              categories[categoryName].forEach(matrixFile => {
                // Extract the base name from matrix file name
                // Handle patterns like:
                // - "criticality_matrix_mindchip-backend-bom_enriched.png"
                // - "effort_matrix_mindchip-backend-bom_enriched.png"
                // - "some_matrix_mindchip-ui-bom_enriched.png"
                const match = matrixFile.fileName.match(/.*_matrix_(.+)\.(png|jpg|jpeg)$/i);
                if (match) {
                  const baseFileName = match[1];

                  // Find the corresponding processed file
                  const targetProcessedFile = Object.keys(this.organizedOutputFiles).find(key => {
                    const keyBase = key.replace(/\.(json|csv|xlsx)$/, '');
                    return (
                      key.includes(baseFileName) ||
                      baseFileName.includes(keyBase) ||
                      keyBase.includes(baseFileName)
                    );
                  });

                  if (targetProcessedFile && targetProcessedFile !== processedFileName) {
                    // Add matrix category to the target processed file
                    if (!this.organizedOutputFiles[targetProcessedFile].matrix) {
                      this.organizedOutputFiles[targetProcessedFile].matrix = [];
                    }
                    this.organizedOutputFiles[targetProcessedFile].matrix.push(matrixFile);

                    // Remove the category from the standalone entry
                    delete this.organizedOutputFiles[processedFileName][categoryName];
                  }
                }
              });

              // If this was a standalone matrix file group and now it's empty, remove it
              const remainingCategories = Object.keys(
                this.organizedOutputFiles[processedFileName],
              ).filter(
                cat =>
                  this.organizedOutputFiles[processedFileName][cat] &&
                  this.organizedOutputFiles[processedFileName][cat].length > 0,
              );

              if (remainingCategories.length === 0) {
                delete this.organizedOutputFiles[processedFileName];
              }
            }
          });
        });

        // Auto-expand if there's only one processed file
        if (Object.keys(this.organizedOutputFiles).length === 1) {
          this.outputFilesPanel = [0];
        }
      } catch (error) {
        console.error('Error loading output files:', error);
        this.log = 'Error loading processed output files';
      } finally {
        this.loadingOutputFiles = false;
        // Eagerly load inline previews for all matrix images
        this.$nextTick(() => this.loadMatrixImages());
      }
    },
    async downloadOutputFile(file) {
      this.$set(this.downloadingFiles, file.filePath, true);
      try {
        const response = await SbomApi.downloadVimpactOutputFile(file.filePath);

        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', file.fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        this.log = `Downloaded: ${file.fileName}`;
      } catch (error) {
        console.error('Error downloading file:', error);
        this.log = `Error downloading: ${file.fileName}`;
      } finally {
        this.$set(this.downloadingFiles, file.filePath, false);
      }
    },

    async loadUploadedEvidenceFileNames() {
      try {
        const response = await evidenceApi.all();
        const evidences = response.data || [];
        this.uploadedEvidenceFileNames = new Set(
          evidences.map(e => e.fileName).filter(name => name && name !== 'null'),
        );
      } catch (error) {
        console.error('Error loading evidence file names:', error);
      }
    },
    isUploadedAsEvidence(fileName) {
      return this.uploadedEvidenceFileNames.has(fileName);
    },
    handleEvidenceCreated(evidence) {
      if (evidence && evidence.fileName && evidence.fileName !== 'null') {
        this.uploadedEvidenceFileNames.add(evidence.fileName);
        // Force reactivity since Set mutations aren't tracked by Vue 2
        this.uploadedEvidenceFileNames = new Set(this.uploadedEvidenceFileNames);
      }
    },
    async uploadFileAsEvidence(file) {
      this.$set(this.evidenceUploadLoading, file.filePath, true);
      try {
        const response = await SbomApi.downloadVimpactOutputFile(file.filePath);
        const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
        const nativeFile = new File([blob], file.fileName, {
          type: blob.type || 'application/octet-stream',
        });

        this.evidenceDialogFile = nativeFile;
        this.evidenceDialogDefaultTitle = file.fileName.replace(/\.[^/.]+$/, '');
        this.evidenceDialogOpen = true;
      } catch (error) {
        console.error('Error preparing file for evidence upload:', error);
        EventBus.$emit('snackbar', {
          text: `Could not load ${file.fileName} for evidence upload`,
          color: 'error',
        });
      } finally {
        this.$set(this.evidenceUploadLoading, file.filePath, false);
      }
    },

    previewFile(file) {
      this.selectedFileForPreview = file;
      this.previewDialog = true;
    },

    async handleFileLoad(file) {
      try {
        const response = await SbomApi.downloadVimpactOutputFile(file.filePath);
        const fileBlob = new Blob([response.data]);

        // Pass the blob to the preview dialog for processing
        if (this.$refs.filePreviewDialog) {
          await this.$refs.filePreviewDialog.processFileData(fileBlob);
        }
      } catch (error) {
        console.error('Error loading file for preview:', error);
        if (this.$refs.filePreviewDialog) {
          this.$refs.filePreviewDialog.error = 'Failed to load file data';
        }
      }
    },

    canPreviewFile(fileName) {
      const extension = fileName
        .split('.')
        .pop()
        ?.toLowerCase();
      return ['csv', 'xlsx', 'xls'].includes(extension);
    },

    getMatrixFilesForSource(categories) {
      // Returns all image files across all categories for a given source SBOM
      return Object.values(categories)
        .flat()
        .filter(file => this.isImageFile(file.fileName));
    },
    isCategoryImages(category) {
      return ['matrix', 'images', 'image', 'criticality', 'effort', 'prioritization'].includes(
        category.toLowerCase(),
      );
    },

    getNonImageFilesCount(categories) {
      return Object.entries(categories).reduce((total, [, files]) => {
        return total + files.filter(f => !this.isImageFile(f.fileName)).length;
      }, 0);
    },

    // Eagerly load blob URLs for all matrix images so they appear inline
    async loadMatrixImages() {
      const tasks = this.allMatrixFiles.map(async ({ file }) => {
        if (this.matrixImageUrls[file.filePath]) return; // already cached
        this.$set(this.matrixImageLoadingStates, file.filePath, 'loading');
        try {
          const response = await SbomApi.downloadVimpactOutputFile(file.filePath);
          const blob = new Blob([response.data], { type: this.getImageMimeType(file.fileName) });
          const url = window.URL.createObjectURL(blob);
          this.$set(this.matrixImageUrls, file.filePath, url);
          this.$set(this.matrixImageLoadingStates, file.filePath, 'ready');
        } catch {
          this.$set(this.matrixImageLoadingStates, file.filePath, 'error');
        }
      });
      await Promise.all(tasks);
    },

    getTotalFilesCount(categories) {
      return Object.values(categories).reduce((total, files) => total + files.length, 0);
    },

    getCategoryLabel(category) {
      const labels = {
        matrix: 'Matrix Images',
        xlsx: 'Excel Reports',
        json: 'JSON Data',
        csv: 'CSV Exports',
      };
      return labels[category] || category.toUpperCase();
    },

    getCategoryIcon(category) {
      const icons = {
        matrix: 'mdi-chart-box',
        xlsx: 'mdi-file-excel',
        json: 'mdi-code-json',
        csv: 'mdi-file-delimited',
      };
      return icons[category] || 'mdi-file';
    },

    getCategoryColor(category) {
      const colors = {
        matrix: 'deep-purple',
        xlsx: 'green',
        json: 'orange',
        csv: 'blue',
      };
      return colors[category] || 'grey';
    },

    getFileTypeIcon(fileName) {
      const extension = fileName
        .split('.')
        .pop()
        .toLowerCase();
      const icons = {
        xlsx: 'mdi-file-excel',
        csv: 'mdi-file-delimited',
        json: 'mdi-code-json',
        txt: 'mdi-file-document',
        pdf: 'mdi-file-pdf-box',
        png: 'mdi-image',
        jpg: 'mdi-image',
        jpeg: 'mdi-image',
      };
      return icons[extension] || 'mdi-file';
    },

    getFileTypeColor(fileName) {
      const extension = fileName
        .split('.')
        .pop()
        .toLowerCase();
      const colors = {
        xlsx: 'green',
        csv: 'blue',
        json: 'orange',
        txt: 'grey',
        pdf: 'red',
        png: 'purple',
        jpg: 'purple',
        jpeg: 'purple',
      };
      return colors[extension] || 'grey';
    },
    isImageFile(fileName) {
      const imageExtensions = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg', 'webp'];
      const extension = fileName
        .split('.')
        .pop()
        .toLowerCase();
      return imageExtensions.includes(extension);
    },
    async previewImage(file) {
      this.$set(this.downloadingFiles, file.filePath, true);

      try {
        const response = await SbomApi.downloadVimpactOutputFile(file.filePath);

        // Create a blob URL for the image
        const blob = new Blob([response.data], { type: this.getImageMimeType(file.fileName) });
        const imageUrl = window.URL.createObjectURL(blob);

        // Generate title and description based on file name
        const imageTitle = this.generateImageTitle(file.fileName);
        const imageDescription = this.generateImageDescription(file.fileName);

        // Show the image preview modal
        EventBus.$emit('show-image-preview', {
          url: imageUrl,
          title: imageTitle,
          description: imageDescription,
          fileSize: this.formatFileSize(blob.size),
        });
      } catch (error) {
        console.error('Error loading image for preview:', error);
        EventBus.$emit('snackbar', {
          text: 'Error loading image preview',
          color: 'error',
        });
      } finally {
        this.$set(this.downloadingFiles, file.filePath, false);
      }
    },
    getImageMimeType(fileName) {
      const extension = fileName
        .split('.')
        .pop()
        .toLowerCase();
      const mimeTypes = {
        png: 'image/png',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        gif: 'image/gif',
        bmp: 'image/bmp',
        svg: 'image/svg+xml',
        webp: 'image/webp',
      };
      return mimeTypes[extension] || 'image/png';
    },
    generateImageTitle(fileName) {
      // Extract meaningful title from filename
      // Handle patterns like: "prioritization_matrix_filename.png", "criticality_matrix_filename.png"
      const baseName = fileName.replace(/\.(png|jpg|jpeg|gif|bmp|svg|webp)$/i, '');

      if (baseName.includes('prioritization_matrix')) {
        return 'Prioritization Matrix';
      } else if (baseName.includes('criticality_matrix')) {
        return 'Criticality Matrix';
      } else if (baseName.includes('effort_matrix')) {
        return 'Effort Matrix';
      } else if (baseName.includes('mitigation_matrix')) {
        return 'Mitigation Matrix';
      } else if (baseName.includes('matrix')) {
        return 'Vulnerability Matrix';
      }

      // Default title
      return 'Matrix Visualization';
    },
    generateImageDescription(fileName) {
      const baseName = fileName.replace(/\.(png|jpg|jpeg|gif|bmp|svg|webp)$/i, '');

      if (baseName.includes('prioritization_matrix')) {
        return 'Interactive scatter plot showing the relationship between effort and criticality scores for vulnerability prioritization';
      } else if (baseName.includes('criticality_matrix')) {
        return 'Visualization of vulnerability criticality scores across different CVEs';
      } else if (baseName.includes('effort_matrix')) {
        return 'Matrix showing effort requirements for mitigating different vulnerabilities';
      } else if (baseName.includes('mitigation_matrix')) {
        return 'Prioritized mitigation order visualization based on criticality and effort analysis';
      }

      return 'Generated matrix visualization for vulnerability impact analysis';
    },
  },
};
</script>

<style scoped>
/* ============================================
   SBOM PAGE - Modern Design System
   Matching DashboardContent.vue patterns
   ============================================ */

/* Breadcrumbs */
.breadcrumbs-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 20px;
  padding-bottom: 40px;
}

.breadcrumbs-container .v-breadcrumbs {
  background-color: #ffffff;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 800;
}

/* ============================================
   HERO CARD
   ============================================ */
.sbom-hero-card {
  border-radius: 16px !important;
  overflow: hidden;
  border: none !important;
  background: #ffffff;
}

.sbom-hero-header {
  background: linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%);
  padding: 24px 28px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.sbom-hero-header-content {
  flex: 1;
}

.sbom-hero-logo {
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(4px);
}

.sbom-hero-title {
  color: white;
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.5px;
}

.sbom-hero-subtitle {
  color: rgba(255, 255, 255, 0.85);
  font-size: 1rem;
  margin: 0;
  font-weight: 400;
}

.sbom-intro-text {
  color: #424242;
  line-height: 1.7;
  font-size: 1.05rem;
}

/* ============================================
   WORKFLOW SECTION
   ============================================ */
.sbom-workflow-section {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 16px;
  padding: 28px;
  margin-top: 8px;
}

.sbom-section-header {
  display: flex;
  align-items: center;
}

.sbom-workflow-steps {
  position: relative;
  display: flex;
  align-items: stretch;
  justify-content: center;
}

.sbom-ws {
  background: white;
  border-radius: 16px;
  padding: 24px 20px;
  text-align: center;
  position: relative;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.sbom-ws:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
}

.sbom-ws.step-upload:hover {
  background: linear-gradient(135deg, #ffffff 0%, #e3f2fd 100%);
}

.sbom-ws.step-process:hover {
  background: linear-gradient(135deg, #ffffff 0%, #fff3e0 100%);
}

.sbom-ws.step-analyze:hover {
  background: linear-gradient(135deg, #ffffff 0%, #e8f5e9 100%);
}

.sbom-step-number {
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #1976d2, #42a5f5);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.875rem;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.4);
}

.sbom-step-number.process-num {
  background: linear-gradient(135deg, #ff8f00, #ffb74d);
  box-shadow: 0 4px 12px rgba(255, 143, 0, 0.4);
}

.sbom-step-number.analyze-num {
  background: linear-gradient(135deg, #2e7d32, #66bb6a);
  box-shadow: 0 4px 12px rgba(46, 125, 50, 0.4);
}

.sbom-step-icon-wrapper {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 12px 0 16px 0;
  transition: all 0.3s ease;
}

.sbom-step-icon-wrapper.upload-icon {
  background: linear-gradient(135deg, #1976d2, #42a5f5);
  box-shadow: 0 8px 24px rgba(25, 118, 210, 0.3);
}

.sbom-step-icon-wrapper.process-icon {
  background: linear-gradient(135deg, #ff8f00, #ffb74d);
  box-shadow: 0 8px 24px rgba(255, 143, 0, 0.3);
}

.sbom-step-icon-wrapper.analyze-icon {
  background: linear-gradient(135deg, #2e7d32, #66bb6a);
  box-shadow: 0 8px 24px rgba(46, 125, 50, 0.3);
}

.sbom-ws:hover .sbom-step-icon-wrapper {
  transform: scale(1.05);
}

.sbom-step-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1a237e;
  margin-bottom: 12px;
  line-height: 1.3;
}

.sbom-step-description {
  color: #616161;
  font-size: 0.875rem;
  line-height: 1.6;
  flex-grow: 1;
  margin-bottom: 8px;
}

/* Arrow connectors */
.sbom-workflow-col {
  position: relative;
}

.sbom-workflow-col:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 50%;
  right: -12px;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 12px solid transparent;
  border-bottom: 12px solid transparent;
  border-left: 14px solid #1976d2;
  z-index: 10;
  filter: drop-shadow(2px 0 4px rgba(25, 118, 210, 0.3));
}

@media (max-width: 960px) {
  .sbom-workflow-col:not(:last-child)::after {
    display: none;
  }
}

/* ============================================
   FEATURE BOXES
   ============================================ */
.sbom-feature-box {
  background: #ffffff;
  border-radius: 12px;
  padding: 20px;
  height: 100%;
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.sbom-feature-box:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  border-color: #7c4dff;
}

.sbom-feature-box.tools-box:hover {
  border-color: #3d5afe;
}

.sbom-feature-box-header {
  display: flex;
  align-items: center;
}

.sbom-feature-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.sbom-feature-list li {
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f5f5f5;
  color: #424242;
  font-size: 0.9rem;
  line-height: 1.5;
}

.sbom-feature-list li:last-child {
  border-bottom: none;
}

/* ============================================
   METRIC CARDS
   ============================================ */
.sbom-metric-card {
  height: 100%;
  border-radius: 12px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  transition: transform 0.3s, box-shadow 0.3s;
  overflow: hidden;
}

.sbom-metric-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15) !important;
}

.primary-grad {
  background: linear-gradient(135deg, #1a237e 0%, #5c6bc0 100%);
}

.secondary-grad {
  background: linear-gradient(135deg, #ff8f00 0%, #ffca28 100%);
}

.accent-grad {
  background: linear-gradient(135deg, #00695c 0%, #4db6ac 100%);
}

.success-grad {
  background: linear-gradient(135deg, #5702f5e1 0%, #9757aa 100%);
}

/* ============================================
   SECTION TITLES (gradient bar)
   ============================================ */
.sbom-section-title {
  background: linear-gradient(135deg, #1a237e 0%, #283593 100%);
  color: white !important;
  font-weight: 600;
  border-radius: 4px 4px 0 0;
}

/* ============================================
   UPLOAD ZONE
   ============================================ */
.sbom-upload-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.sbom-upload-zone {
  text-align: center;
  padding: 48px 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 16px;
  border: 2px dashed #bdbdbd;
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
}

.sbom-upload-zone:hover {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-color: #1976d2;
}

.sbom-upload-zone.drag-over {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-color: #1976d2;
  transform: scale(1.01);
}

.sbom-upload-icon-wrapper {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1976d2, #42a5f5);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px auto;
  box-shadow: 0 8px 24px rgba(25, 118, 210, 0.3);
  transition: transform 0.3s ease;
}

.sbom-upload-zone:hover .sbom-upload-icon-wrapper {
  transform: scale(1.1);
}

.sbom-browse-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  padding: 0 24px !important;
}

/* ============================================
   FILES LIST CARD
   ============================================ */
.sbom-files-card {
  border-radius: 16px !important;
  overflow: hidden;
}

/* ── List header (shared) ─────────────────── */
.sbom-list-header {
  display: flex;
  align-items: center;
  padding: 14px 20px;
  gap: 12px;
}

.sbom-list-header--raw {
  background: linear-gradient(135deg, #4527a0 0%, #5e35b1 100%);
}

.sbom-list-header--enriched {
  background: linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%);
}

.sbom-list-del-btn {
  opacity: 0.85;
  text-transform: none !important;
  font-size: 12px !important;
  letter-spacing: 0 !important;
}

/* ── File rows ────────────────────────────── */
.sbom-file-row {
  display: flex;
  align-items: center;
  padding: 12px 18px;
  border-bottom: 1px solid #f0f4f8;
  transition: background 0.18s ease;
  position: relative;
}

.sbom-file-row:last-child {
  border-bottom: none;
}

.sbom-file-row:hover {
  background: #f5f0fc;
}

.sbom-file-row--enriched:hover {
  background: #e8eaf6;
}

.sbom-file-row--enriching {
  background: #f3e5f5 !important;
}

.sbom-file-row--processing {
  background: #e3f2fd !important;
}

.sbom-file-row--done {
  background: #f1f8e9 !important;
}

.sbom-file-info {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.sbom-file-meta {
  min-width: 0;
}

.sbom-file-name {
  font-size: 13px;
  font-weight: 600;
  color: #303030;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 340px;
}

.sbom-file-sub {
  font-size: 11px;
  color: #757575;
  margin-top: 2px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.sbom-file-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

/* ── Enriched badge icon ──────────────────── */
.sbom-enriched-badge {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #3949ab 0%, #5c6bc0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* ── Action buttons in rows ──────────────── */
.sbom-action-chip {
  text-transform: none !important;
  font-size: 12px !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  padding: 0 12px !important;
}

.sbom-action-chip--enrich {
  /* deep-purple primary action in raw list */
}

/* ── Batch process button ─────────────────── */
.sbom-batch-enrich-btn {
  text-transform: none !important;
  font-weight: 700 !important;
  font-size: 12px !important;
  border-radius: 8px !important;
  color: #3949ab !important;
  letter-spacing: 0 !important;
}

/* ── Processing bar inside row ───────────── */
.sbom-processing-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
}

/* ── Row enter/leave animation ───────────── */
.sbom-row-enter-active,
.sbom-row-leave-active {
  transition: all 0.3s ease;
}

.sbom-row-enter,
.sbom-row-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.sbom-select-all-checkbox {
  flex: none !important;
}

.sbom-select-all-checkbox >>> .v-input--selection-controls__input .v-icon {
  color: rgba(255, 255, 255, 0.85) !important;
}

.sbom-select-all-checkbox >>> .v-input--selection-controls__ripple {
  color: white !important;
}

.sbom-clear-all-btn {
  color: error !important;
  text-transform: none !important;
  font-weight: 600 !important;
  border: 1px solid rgba(255, 255, 255, 0.4) !important;
  border-radius: 8px !important;
  letter-spacing: 0 !important;
}

.sbom-clear-all-btn:hover {
  background: rgba(240, 26, 26, 0.15) !important;
  border-color: rgba(184, 83, 83, 0.7) !important;
}

.file-item {
  border-bottom: 1px solid #f0f0f0;
  padding: 8px 0;
  transition: background-color 0.2s ease;
}

.file-item:hover {
  background-color: #f8f9fa;
}

.file-item:last-child {
  border-bottom: none;
}

.v-list-item-action {
  margin-left: 8px;
}

.gap-2 > * {
  margin-right: 8px;
}

.gap-2 > *:last-child {
  margin-right: 0;
}

/* ============================================
   BATCH ACTIONS
   ============================================ */
.sbom-batch-card {
  border-radius: 12px !important;
  border-left: 4px solid #1976d2;
  background: linear-gradient(135deg, #e3f2fd 0%, #ffffff 100%);
}

.sbom-action-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
}

/* ============================================
   DEPENDENCY TRACK SECTION
   ============================================ */
.sbom-dt-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.sbom-dt-header {
  background: linear-gradient(135deg, #004d40 0%, #00897b 100%);
  padding: 24px 28px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.sbom-dt-content {
  flex: 1;
}

.sbom-dt-logo-avatar {
  background: rgba(255, 255, 255, 0.15) !important;
  backdrop-filter: blur(4px);
  border-radius: 12px !important;
  padding: 6px;
}

.sbom-dt-logo-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: brightness(0) invert(1);
}

.sbom-dt-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  color: #004d40 !important;
}

/* ============================================
   EMPTY STATE
   ============================================ */
.sbom-empty-state {
  border-radius: 16px !important;
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
}

.sbom-empty-icon-wrapper {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: linear-gradient(135deg, #bdbdbd, #e0e0e0);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

/* ============================================
   OUTPUT FILES
   ============================================ */
.sbom-output-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.sbom-refresh-btn {
  text-transform: none !important;
  font-weight: 500;
}

.output-category-card {
  transition: all 0.3s ease;
  border-radius: 12px !important;
}

.output-category-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
}

/* ============================================
   DIALOGS
   ============================================ */
.sbom-dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.sbom-dialog-title {
  background: linear-gradient(135deg, #1a237e 0%, #3949ab 100%);
  color: white !important;
  font-weight: 600;
}

.sbom-dialog-title-success {
  background: linear-gradient(135deg, #2e7d32 0%, #66bb6a 100%);
  color: white !important;
  font-weight: 600;
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 960px) {
  .sbom-hero-header {
    flex-direction: column;
    text-align: center;
  }

  .sbom-hero-header-content {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .sbom-dt-header {
    flex-direction: column;
    text-align: center;
  }

  .sbom-ws {
    margin-bottom: 24px;
  }
}

@media (max-width: 600px) {
  .sbom-hero-title {
    font-size: 1.4rem;
  }

  .sbom-workflow-section {
    padding: 20px 16px;
  }

  .sbom-step-icon-wrapper {
    width: 64px;
    height: 64px;
  }

  .sbom-step-icon-wrapper .v-icon {
    font-size: 32px !important;
  }

  .sbom-upload-zone {
    padding: 32px 16px;
  }
}

/* ═══════════════════════════════════════════════════
   VIMPACT MATRIX HERO — top of page, prominent tiles
   ═══════════════════════════════════════════════════ */

.sbom-matrix-hero-root {
  border-radius: 20px;
  overflow: hidden;
  background: linear-gradient(145deg, #0d1b4b 0%, #1a237e 40%, #00695c 100%);
  box-shadow: 0 8px 40px rgba(26, 35, 126, 0.3);
  margin-bottom: 6px;
}

.sbom-matrix-hero-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px 14px 24px;
  flex-wrap: wrap;
  gap: 10px;
}

.sbom-matrix-hero-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.sbom-matrix-hero-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.sbom-matrix-hero-title {
  color: white;
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
  line-height: 1.25;
}

.sbom-matrix-hero-subtitle {
  margin: 2px 0 0 0;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 400;
}

.sbom-matrix-hero-header-right {
  display: flex;
  align-items: center;
}

.sbom-matrix-count-chip {
  background: rgba(255, 255, 255, 0.22) !important;
  color: #ffffff !important;
  font-weight: 700 !important;
  backdrop-filter: blur(4px);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
}

.sbom-matrix-refresh-btn {
  opacity: 0.85;
}

.sbom-matrix-refresh-btn:hover {
  opacity: 1;
}

.sbom-matrix-loading {
  padding: 0 16px 20px 16px;
}

.sbom-matrix-skeleton {
  border-radius: 16px !important;
}

/* Gallery grid */
.sbom-matrix-gallery {
  padding: 0 12px 16px 12px;
}

/* Individual matrix card */
.sbom-matrix-card {
  border-radius: 16px;
  overflow: hidden;
  background: #1e2a5e;
  position: relative;
  cursor: pointer;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  aspect-ratio: 16 / 10;
  display: flex;
  flex-direction: column;
}

.sbom-matrix-card:hover {
  transform: scale(1.02);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
}

.sbom-matrix-card:hover .sbom-matrix-hover-overlay {
  opacity: 1;
}

/* Color accent per index */
.sbom-matrix-card--0 {
  border-top: 3px solid #7c4dff;
}
.sbom-matrix-card--1 {
  border-top: 3px solid #00bcd4;
}
.sbom-matrix-card--2 {
  border-top: 3px solid #ff6f00;
}
.sbom-matrix-card--3 {
  border-top: 3px solid #4caf50;
}

/* Card header badges */
.sbom-matrix-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(6px);
  flex-shrink: 0;
  z-index: 2;
  position: relative;
}

.sbom-matrix-card-type-badge {
  display: flex;
  align-items: center;
  font-size: 0.72rem;
  font-weight: 700;
  color: white;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.sbom-matrix-card-source-badge {
  display: flex;
  align-items: center;
  font-size: 0.66rem;
  color: rgba(255, 255, 255, 0.65);
  max-width: 140px;
  overflow: hidden;
}

.sbom-matrix-card-source-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Image container */
.sbom-matrix-img-container {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #0d1235;
  min-height: 0;
}

.sbom-matrix-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  transition: transform 0.4s ease;
}

.sbom-matrix-card:hover .sbom-matrix-img {
  transform: scale(1.03);
}

.sbom-matrix-img-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: 120px;
  background: rgba(13, 18, 53, 0.8);
}

.sbom-matrix-img-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: 120px;
}

.sbom-matrix-img-error-text {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 8px;
}

/* Footer overlay */
.sbom-matrix-card-footer {
  background: linear-gradient(
    0deg,
    rgba(0, 0, 0, 0.85) 0%,
    rgba(0, 0, 0, 0.4) 70%,
    transparent 100%
  );
  padding: 24px 14px 12px 14px;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 2;
  transition: opacity 0.3s ease;
}

.sbom-matrix-card-desc {
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.4;
  margin: 0 0 8px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.sbom-matrix-card-actions {
  display: flex;
  align-items: center;
}

.sbom-matrix-preview-btn {
  background: rgba(124, 77, 255, 0.85) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 0.7rem !important;
  backdrop-filter: blur(4px);
}

.sbom-matrix-dl-btn {
  border-color: rgba(255, 255, 255, 0.4) !important;
  text-transform: none !important;
  font-size: 0.7rem !important;
  font-weight: 600 !important;
}

/* Hover overlay */
.sbom-matrix-hover-overlay {
  position: absolute;
  inset: 0;
  background: rgba(124, 77, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 3;
  pointer-events: none;
  border-radius: 16px;
}

/* ═══════════════════════════════════════════
   PROCESSED OUTPUT FILES — Redesigned
   ═══════════════════════════════════════════ */

.sbom-output-header {
  background: linear-gradient(135deg, #1a237e 0%, #283593 100%);
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 16px 16px 0 0;
}

.sbom-output-header-left {
  display: flex;
  align-items: center;
}

.sbom-output-header-title {
  color: white;
  font-size: 0.9rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.sbom-output-count-chip {
  background: rgba(255, 255, 255, 0.22) !important;
  color: #ffffff !important;
  font-weight: 700 !important;
  font-size: 0.72rem !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.sbom-output-refresh-btn {
  opacity: 0.85;
  text-transform: none !important;
  font-size: 0.8rem !important;
}

.sbom-output-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
}

.sbom-output-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  background: #fafafa;
}

.sbom-output-empty-title {
  font-size: 1rem;
  font-weight: 700;
  color: #757575;
  margin: 0 0 6px 0;
}

.sbom-output-empty-hint {
  font-size: 0.8rem;
  color: #9e9e9e;
  margin: 0;
  text-align: center;
}

/* Tabs (kept for reference, no longer used in main output) */
.sbom-output-tabs {
  border-bottom: 1px solid #e0e0e0;
}

.sbom-output-tab {
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 0.82rem !important;
  letter-spacing: 0 !important;
  min-width: 0 !important;
  max-width: 260px !important;
}

.sbom-output-tab-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 180px;
  display: inline-block;
}

.sbom-output-tab-count {
  background: #e8eaf6 !important;
  color: #3949ab !important;
  font-weight: 700 !important;
  flex-shrink: 0;
}

.sbom-output-tabs-items {
  background: transparent !important;
}

.sbom-output-categories-wrap {
  background: #fafafa;
}

/* Accordion panels */
.sbom-output-panels-wrap {
  padding: 12px 16px 16px;
}

.sbom-output-panel {
  border: 1px solid #e8eaf6 !important;
  border-radius: 10px !important;
  margin-bottom: 8px !important;
  overflow: hidden;
  background: white !important;
}

.sbom-output-panel::before {
  box-shadow: none !important;
}

.sbom-output-panel-header {
  background: linear-gradient(135deg, #f5f7ff 0%, #eef1ff 100%) !important;
  border-radius: 10px 10px 0 0 !important;
  min-height: 52px !important;
}

.sbom-output-panel-avatar {
  background: linear-gradient(135deg, #1a237e, #3949ab) !important;
  border-radius: 6px !important;
  flex-shrink: 0 !important;
}

.sbom-output-panel-name {
  font-weight: 600;
  font-size: 0.88rem;
  color: #1a237e;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.sbom-output-panel-img-chip {
  background: #ede7f6 !important;
  color: #512da8 !important;
  font-weight: 700 !important;
}

.sbom-output-panel-file-chip {
  background: #e8eaf6 !important;
  color: #283593 !important;
  font-weight: 700 !important;
}

.sbom-output-panel-content .v-expansion-panel-content__wrap {
  padding: 16px 16px 12px !important;
}

/* Inline matrix section inside accordion */
.sbom-inline-matrix-section {
  margin-bottom: 8px;
}

.sbom-inline-matrix-label {
  display: flex;
  align-items: center;
  font-weight: 700;
  font-size: 0.82rem;
  color: #512da8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 10px;
  gap: 2px;
}

.sbom-inline-matrix-hint {
  font-size: 0.72rem;
  color: #9e9e9e;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  margin-left: 6px;
}

.sbom-inline-matrix-row {
  margin-bottom: 4px;
}

/* Side-by-side panel layout */
.sbom-panel-body-row {
  align-items: flex-start;
}

.sbom-panel-images-col {
  padding-top: 4px;
}

.sbom-panel-files-col {
  padding-top: 4px;
}

.sbom-panel-files-label {
  display: flex;
  align-items: center;
  font-weight: 700;
  font-size: 0.78rem;
  color: #1a237e;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 2px solid #e8eaf6;
}

/* Category block */
.sbom-output-category-block {
  background: white;
  border-radius: 10px;
  border: 1px solid #e8eaf6;
  overflow: hidden;
  margin-bottom: 2px;
}

.sbom-output-category-label {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #f5f5f5;
  border-bottom: 1px solid #eeeeee;
  font-size: 0.78rem;
  font-weight: 700;
  color: #424242;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  gap: 4px;
}

/* File rows inside category */
.sbom-output-file-row {
  display: flex;
  align-items: center;
  padding: 7px 12px;
  border-bottom: 1px solid #f5f5f5;
  transition: background 0.15s ease;
}

.sbom-output-file-row:last-child {
  border-bottom: none;
}

.sbom-output-file-row:hover {
  background: #f3f8ff;
}

.sbom-output-file-name {
  flex: 1;
  font-size: 0.78rem;
  font-weight: 500;
  color: #37474f;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.sbom-output-file-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 2px;
  opacity: 0.5;
  transition: opacity 0.2s ease;
}

.sbom-output-file-row:hover .sbom-output-file-actions {
  opacity: 1;
}

.sbom-output-file-btn {
  transition: background 0.2s ease !important;
}

.sbom-output-file-btn:hover {
  background: #e8eaf6 !important;
}

/* Responsive */
@media (max-width: 960px) {
  .sbom-matrix-hero-header {
    flex-direction: column;
    align-items: flex-start;
  }
  .sbom-matrix-card {
    aspect-ratio: 16 / 11;
  }
}

@media (max-width: 600px) {
  .sbom-matrix-card {
    aspect-ratio: 4 / 3;
  }
  .sbom-matrix-hero-title {
    font-size: 1rem;
  }
  .sbom-output-tab-name {
    max-width: 100px;
  }
}
</style>
